import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@/lib/adminApi';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ScrollText, RefreshCw } from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { AdminTeamMember } from '@/lib/adminApi';

const ALL_PEOPLE = 'all';

/** Short human summary of what a row changed, from its payload. */
function payloadSummary(payload: Record<string, unknown> | null): string | null {
  if (!payload) return null;
  const parts: string[] = [];
  const name = payload.name ?? payload.display_name ?? payload.brand_name ?? payload.subject ?? payload.email;
  if (typeof name === 'string' && name) parts.push(name);
  if (typeof payload.previous_role === 'string' && typeof payload.role === 'string') {
    parts.push(`${payload.previous_role} to ${payload.role}`);
  } else if (typeof payload.role === 'string') {
    parts.push(payload.role);
  }
  if (Array.isArray(payload.areas)) parts.push(`areas: ${payload.areas.join(', ')}`);
  if (Array.isArray(payload.changed) && payload.changed.length) {
    parts.push(`changed ${payload.changed.slice(0, 6).join(', ')}${payload.changed.length > 6 ? '…' : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

interface AuditEntry {
  id: string;
  // System-generated audit rows (triggers, cron jobs) have a null admin_id.
  admin_id: string | null;
  action: string;
  // Some legacy rows may not record a target_table — keep it optional.
  target_table: string | null;
  target_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  // Joined admin identity (populated by `list_audit_log_admin`).
  // Null for system-authored rows or for admins who've since
  // been removed from `platform_admins` (the audit row stays —
  // the join just returns NULL).
  admin_full_name?: string | null;
  admin_avatar_url?: string | null;
  admin_email?: string | null;
}

const actionColors: Record<string, string> = {
  create: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  update: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  delete: 'bg-red-500/10 text-red-400 border-red-500/20',
  suspend: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

function getActionColor(action: string): string {
  for (const [key, color] of Object.entries(actionColors)) {
    if (action.includes(key)) return color;
  }
  return 'bg-gray-700/50 text-gray-400 border-gray-600';
}

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState<string>(
    () => new URLSearchParams(window.location.search).get('admin') || ALL_PEOPLE,
  );
  const [team, setTeam] = useState<AdminTeamMember[]>([]);
  const limit = 25;

  useEffect(() => {
    adminApi.getAdminTeam().then((r) => setTeam(r.members)).catch(() => {});
  }, []);

  const teamName = (id: string | null) => team.find((m) => m.user_id === id)?.display_name ?? null;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAuditLog({
        page,
        limit,
        admin_id: person === ALL_PEOPLE ? undefined : person,
      });
      // RPC returns rows typed as `Record<string, unknown>` —
      // cast to the page-level `AuditEntry` shape. The RPC
      // contract is enforced server-side; mismatched fields
      // here would simply render as undefined, never crash.
      setEntries(((res.data as unknown) as AuditEntry[]) ?? []);
      setTotal(res.total || 0);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, person]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / limit);

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Audit Log</h1>
            <p className="text-sm text-gray-400 mt-1">Complete history of admin actions</p>
          </div>
          {/*
            Manual refresh — the audit log isn't realtime by
            design (operators come here to investigate, not
            babysit). Click after triggering an action to
            confirm the row landed.
          */}
          <div className="flex items-center gap-2">
          <Select
            value={person}
            onValueChange={(v) => {
              setPage(1);
              setPerson(v);
            }}
          >
            <SelectTrigger className="w-48 bg-gray-900 border-gray-800 text-white">
              <SelectValue placeholder="Everyone" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value={ALL_PEOPLE} className="text-gray-300 focus:bg-gray-800 focus:text-white">Everyone</SelectItem>
              {team.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                  {m.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="text-gray-400 hover:text-white"
            title="Refresh audit log"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          </div>
        </div>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="p-12 text-center">
                <ScrollText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No audit entries yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Timestamp</TableHead>
                    <TableHead className="text-gray-400">Action</TableHead>
                    <TableHead className="text-gray-400">Table</TableHead>
                    <TableHead className="text-gray-400">Target ID</TableHead>
                    <TableHead className="text-gray-400">Admin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} className="border-gray-800 hover:bg-gray-800/30">
                      <TableCell className="text-gray-400 text-sm font-mono">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : '–'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionColor(e.action || '')}>
                          {e.action || 'unknown'}
                        </Badge>
                        {payloadSummary(e.payload) && (
                          <p className="text-xs text-gray-500 mt-1 max-w-[320px] truncate">{payloadSummary(e.payload)}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        {e.target_table || '–'}
                      </TableCell>
                      <TableCell className="text-gray-400 text-xs font-mono">
                        {e.target_id ? `${e.target_id.slice(0, 12)}...` : '–'}
                      </TableCell>
                      <TableCell>
                        {/*
                          Identity priority:
                            1. system — admin_id is null (trigger /
                               cron / SQL-side automation).
                            2. avatar + name + email tooltip — normal
                               admin action.
                            3. avatar + email — admin row with no
                               full_name in profiles.
                            4. truncated UUID — auth.users row has
                               been deleted but the audit row
                               survived (audit log is immutable, by
                               design — we never lose the trace).
                        */}
                        {!e.admin_id ? (
                          <span className="text-gray-600 italic text-sm">system</span>
                        ) : teamName(e.admin_id) || e.admin_full_name || e.admin_email ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 cursor-help">
                                <UserAvatar
                                  size="sm"
                                  name={teamName(e.admin_id) || e.admin_full_name || e.admin_email || 'Admin'}
                                  src={e.admin_avatar_url ?? null}
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-white text-sm font-medium truncate max-w-[180px]">
                                    {teamName(e.admin_id) || e.admin_full_name || e.admin_email}
                                  </span>
                                  {(teamName(e.admin_id) || e.admin_full_name) && e.admin_email && (
                                    <span className="text-gray-500 text-xs truncate max-w-[180px]">
                                      {e.admin_email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="bg-gray-900 border-gray-700 text-white">
                              <p className="text-xs font-mono">{e.admin_id}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-gray-400 text-xs font-mono cursor-help">
                                {e.admin_id.slice(0, 8)}...
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="bg-gray-900 border-gray-700 text-white">
                              <p className="text-xs">Admin account no longer exists ({e.admin_id})</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{total} entries total</p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-gray-400">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="text-gray-400">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
